import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Building2, MapPin, CreditCard, Shield, Zap } from 'lucide-react';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { createBrand, getBrandProfile, updateBrand } from '../../store/actions';

const TABS = [
  { id: 'profile',   label: 'Brand Profile',    icon: Building2  },
  // { id: 'addresses', label: 'Addresses',         icon: MapPin     },
  // { id: 'payment',   label: 'Payment & Wallet',  icon: CreditCard },
  // { id: 'security',  label: 'Security',          icon: Shield     },
  // { id: 'commerce',  label: 'Kriha Commerce',    icon: Zap        },
];
const inputCls = 'w-full mt-1.5 px-3 py-2.5 text-sm border border-[#E5E3DE] rounded-lg outline-none focus:border-[#3D52A0]';

function ProfileForm({ profile }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    name:               profile?.name               ?? '',
    legalName:          profile?.legalName           ?? '',
    tradingName:        profile?.tradingName         ?? '',
    gstNumber:          profile?.gstNumber           ?? '',
    registrationNumber: profile?.registrationNumber  ?? '',
    email:              profile?.email               ?? '',
    phone:              profile?.phone               ?? '',
    website:            profile?.website             ?? '',
    currency:           profile?.currency            ?? 'INR',
    timezone:           profile?.timezone            ?? 'Asia/Kolkata',
    category:           profile?.category            ?? '',
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = (e) => {
    e.preventDefault();
    const payload = { ...form, category: form.category === '' ? null : form.category };
    const refresh = { success: () => dispatch(getBrandProfile()) };
    if (profile) dispatch(updateBrand({ payload: { id: profile.id, ...payload }, callback: refresh }));
    else dispatch(createBrand({ payload: { ...payload, status: 'Active', onboardingStatus: 'Complete' }, callback: refresh }));
  };

  return (
    <form onSubmit={save} className="bg-white rounded-xl border border-[#E5E3DE] p-6 space-y-4">
      <h3 className="font-semibold text-[#1A1915]" style={{ fontFamily: 'Manrope, sans-serif' }}>Brand Profile</h3>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">Brand name</label>
          <input required value={form.name} onChange={set('name')} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">Legal name</label>
          <input value={form.legalName} onChange={set('legalName')} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">Trading name</label>
          <input value={form.tradingName} onChange={set('tradingName')} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">GST number</label>
          <input value={form.gstNumber} onChange={set('gstNumber')} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">Registration number</label>
          <input value={form.registrationNumber} onChange={set('registrationNumber')} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">Primary email</label>
          <input type="email" value={form.email} onChange={set('email')} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">Phone</label>
          <input value={form.phone} onChange={set('phone')} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">Website</label>
          <input value={form.website} onChange={set('website')} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">Default currency</label>
          <select value={form.currency} onChange={set('currency')} className={inputCls}>
            <option value="INR">INR — Indian Rupee</option>
            <option value="USD">USD — US Dollar</option>
            <option value="EUR">EUR — Euro</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5A54]">Time zone</label>
          <select value={form.timezone} onChange={set('timezone')} className={inputCls}>
            <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
            <option value="Asia/Dubai">Asia/Dubai (GST)</option>
            <option value="Europe/London">Europe/London (GMT)</option>
            <option value="America/New_York">America/New_York (EST)</option>
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-[#5C5A54]">Brand category</label>
        <input value={form.category} onChange={set('category')} className={inputCls} />
      </div>

      <Button type="submit" variant="primary" size="sm">Save changes</Button>
    </form>
  );
}

export default function BrandSettings() {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.brand.profile);
  const [activeTab, setActiveTab] = useState('profile');
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getBrandProfile({ callback: { success: () => setChecked(true), failure: () => setChecked(true) } }));
    }, 50);
    return () => clearTimeout(timer);
  }, [dispatch]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title="Brand Settings" subtitle={profile?.legalName || profile?.name || ''} />
      <div className="flex-1 overflow-hidden flex">
        <div className="w-52 border-r border-[#E5E3DE] p-3 space-y-0.5 shrink-0">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeTab === tab.id ? 'bg-[#EEF0FB] text-[#3D52A0]' : 'text-[#5C5A54] hover:bg-[#F4F3F0]'}`}
            >
              <tab.icon size={15} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'profile' && (
            <div className="max-w-2xl">
              {!checked && !profile ? <p className="text-sm text-[#9B9893]">Loading…</p> : <ProfileForm key={profile?.id ?? 'new'} profile={profile} />}
            </div>
          )}

          {/* {activeTab === 'addresses' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-xl border border-[#E5E3DE] p-8 text-center space-y-3">
                <MapPin size={28} className="text-[#9B9893] mx-auto" />
                <p className="text-sm text-[#5C5A54]">Manage delivery, billing, and warehouse addresses.</p>
                <Button variant="outline" size="sm">Configure</Button>
              </div>
            </div>
          )} */}

          {/* {activeTab === 'payment' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-xl border border-[#E5E3DE] p-8 text-center space-y-3">
                <CreditCard size={28} className="text-[#9B9893] mx-auto" />
                <p className="text-sm text-[#5C5A54]">View payment instructions and accepted payment methods from your manufacturer.</p>
                <Button variant="outline" size="sm">Configure</Button>
              </div>
            </div>
          )} */}

          {/* {activeTab === 'security' && (
            <div className="max-w-2xl space-y-4">
              <div className="bg-white rounded-xl border border-[#E5E3DE] p-6 space-y-4">
                <h3 className="font-semibold text-[#1A1915]" style={{ fontFamily: 'Manrope, sans-serif' }}>Security Settings</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Two-factor authentication', desc: 'Add an extra layer of security to your account', status: 'enabled' },
                    { label: 'Active sessions', desc: '2 active sessions across devices', status: null },
                    { label: 'Login history', desc: 'View recent login activity', status: null },
                    { label: 'Session timeout', desc: 'Auto-logout after 8 hours of inactivity', status: null },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between p-4 border border-[#E5E3DE] rounded-xl">
                      <div>
                        <p className="text-sm font-semibold text-[#1A1915]">{item.label}</p>
                        <p className="text-xs text-[#9B9893]">{item.desc}</p>
                      </div>
                      {item.status === 'enabled' ? (
                        <Badge variant="green">Enabled</Badge>
                      ) : (
                        <Button variant="secondary" size="xs">Manage</Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )} */}

          {/* {activeTab === 'commerce' && (
            <div className="max-w-lg bg-white rounded-xl border border-[#E5E3DE] p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-[#F0EBFF] rounded-2xl flex items-center justify-center mx-auto"><Zap size={28} className="text-[#5B21B6]" /></div>
              <h3 className="font-bold text-[#1A1915] text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Kriha Commerce</h3>
              <p className="text-sm text-[#5C5A54]">Kriha Commerce allows you to publish your finalised designs and approved products directly to a storefront. Connect your catalogue to online channels from within GarmentOS.</p>
              <Badge variant="purple">Phase 2 — Coming soon</Badge>
              <div className="bg-[#FAFAF8] rounded-xl p-4 text-xs text-left space-y-2">
                <p className="font-semibold text-[#5C5A54]">Eligibility requirements</p>
                <div className="flex items-center gap-2"><span className="text-[#1A6B3E]">✓</span><span>3+ finalised designs</span></div>
                <div className="flex items-center gap-2"><span className="text-[#1A6B3E]">✓</span><span>Active engagement with approved manufacturer</span></div>
                <div className="flex items-center gap-2"><span className="text-[#9B9893]">○</span><span className="text-[#9B9893]">Commerce opt-in not yet enabled</span></div>
              </div>
              <Button variant="secondary" size="sm">Learn more</Button>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
}
